import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag, Calendar, Percent, Zap, ShoppingBag, Ticket, Search, X } from 'lucide-react';

const getDiscountDetail = (discount) => {
    const isBundle = discount.campaignType === 'bundle' || discount.campaignType === 'coupon_bundle';

    if (isBundle) {
        const buyQ = discount.buyQuantity || 1;
        const getQ = discount.getQuantity || 1;
        const disc = discount.bundleDiscountValue || 100;
        const buyNames = (discount.buyItems || []).slice(0, 3).join(', ') || 'seçili ürünler';
        const getNames = (discount.getItems || []).slice(0, 3).join(', ') || 'seçili ürünler';
        const discText = disc === 100 || disc === '100' ? 'bedava' : `%${disc} indirimli`;
        return { label: `${buyQ} adet ${buyNames} al, ${getQ} adet ${getNames} ${discText}`, icon: ShoppingBag, color: '#f59e0b' };
    }

    const isCoupon = discount.campaignType === 'coupon' || discount.campaignType === 'coupon_bundle';

    if (discount.type === 'percentage') {
        const extra = discount.minAmount ? ` (min. ${discount.minAmount} TL)` : '';
        return { label: `Sepette %${discount.value} indirim${extra}`, icon: isCoupon ? Ticket : Percent, color: isCoupon ? '#3b82f6' : '#ef4444' };
    }
    if (discount.type === 'fixed') {
        const extra = discount.minAmount ? ` (min. ${discount.minAmount} TL)` : '';
        return { label: `Sepetten ${discount.value} TL indirim${extra}`, icon: isCoupon ? Ticket : Tag, color: isCoupon ? '#3b82f6' : '#22c55e' };
    }
    if (discount.type === 'multiplier') {
        return { label: `Kazanılan puan ${discount.value}x ile çarpılır`, icon: Zap, color: '#8b5cf6' };
    }
    return null;
};

const CustomerPortalPromotions = ({
    campaigns,
    discounts,
    primaryColor,
    addedCoupons,
    setAddedCoupons,
}) => {
    const { t } = useTranslation();
    const [couponCode, setCouponCode] = useState('');
    const [couponError, setCouponError] = useState('');

    const handleAddCoupon = () => {
        if (!couponCode.trim()) return;
        const code = couponCode.trim().toUpperCase();
        setCouponError('');

        // Zaten eklenmiş mi?
        if (addedCoupons.some(c => c.code === code)) {
            setCouponError('Bu kupon zaten eklenmiş.');
            return;
        }

        // discounts içinden kupon kodunu bul
        const found = (discounts || []).find(d =>
            d.code && d.code.toUpperCase() === code &&
            (d.campaignType === 'coupon' || d.campaignType === 'coupon_bundle') &&
            d.isActive !== false
        );

        if (!found) {
            setCouponError('Geçersiz kupon kodu.');
            return;
        }

        // Süresi geçmiş mi?
        if (found.expiryDate && new Date(found.expiryDate) < new Date()) {
            setCouponError('Bu kuponun süresi dolmuş.');
            return;
        }

        setAddedCoupons(prev => [...prev, found]);
        setCouponCode('');
    };

    const removeCoupon = (id) => {
        setAddedCoupons(prev => prev.filter(c => c.id !== id));
    };


    return (
        <div className="fade-in">
            <div style={{ marginBottom: '2.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1.25rem', padding: '0 0.5rem' }}>
                    <div style={{ background: primaryColor + '15', color: primaryColor, padding: '8px', borderRadius: '12px' }}>
                        <Tag size={20} />
                    </div>
                    <div>
                        <h3 style={{ fontSize: '1.125rem', fontWeight: 800, margin: 0 }}>İndirim, Kampanya & Kupon</h3>
                        <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600, margin: '4px 0 0 0' }}>Kafemize özel güncel fırsatlar</p>
                    </div>
                </div>

                {/* Kupon Kodu Giriş Alanı */}
                <div style={{
                    marginBottom: '1.25rem',
                    padding: '14px 16px',
                    background: '#f0f9ff',
                    borderRadius: '16px',
                    border: '1px dashed #93c5fd'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <Ticket size={16} style={{ color: '#3b82f6' }} />
                        <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1d4ed8' }}>Kupon Kodu Gir</span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <input
                            type="text"
                            value={couponCode}
                            onChange={e => { setCouponCode(e.target.value.toUpperCase()); setCouponError(''); }}
                            onKeyDown={e => e.key === 'Enter' && handleAddCoupon()}
                            placeholder="Kupon kodunuzu girin"
                            style={{
                                flex: 1,
                                padding: '10px 14px',
                                borderRadius: '10px',
                                border: '1px solid #bfdbfe',
                                fontSize: '0.9rem',
                                fontWeight: 700,
                                letterSpacing: '1px',
                                outline: 'none',
                                background: 'white',
                                textTransform: 'uppercase'
                            }}
                        />
                        <button
                            onClick={handleAddCoupon}
                            style={{
                                padding: '10px 20px',
                                borderRadius: '10px',
                                border: 'none',
                                background: '#3b82f6',
                                color: 'white',
                                fontWeight: 700,
                                fontSize: '0.85rem',
                                cursor: 'pointer'
                            }}
                        >
                            Ekle
                        </button>
                    </div>
                    {couponError && (
                        <p style={{ fontSize: '0.75rem', color: '#ef4444', fontWeight: 600, marginTop: '6px', margin: '6px 0 0 0' }}>{couponError}</p>
                    )}
                </div>

                {/* Kampanyalar & İndirimler */}
                {campaigns.length === 0 ? (
                    <div className="card glass" style={{ padding: '2rem', textAlign: 'center', opacity: 0.6 }}>
                        <p style={{ fontSize: '0.875rem', margin: 0 }}>Şu an aktif bir kampanya bulunmuyor.</p>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
                        {campaigns.map(discount => {
                            const isUpcoming = discount.startDate && new Date(discount.startDate) > new Date();
                            const detail = getDiscountDetail(discount);
                            const DetailIcon = detail?.icon;

                            return (
                                <div key={discount.id} className="card-premium" style={{
                                    opacity: isUpcoming ? 0.7 : 1,
                                }}>
                                    <div style={{ flex: 1 }}>
                                        <h4 style={{ fontWeight: 800, fontSize: '1rem', margin: '0 0 0.25rem 0' }}>
                                            {discount.title}
                                            {discount.birthdayOnly && (
                                                <span style={{ marginLeft: '8px', fontSize: '0.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: '8px', background: '#fef3c7', color: '#92400e', verticalAlign: 'middle' }}>🎂</span>
                                            )}
                                        </h4>
                                        {detail && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: '0.375rem 0', padding: '6px 10px', background: `${detail.color}08`, borderRadius: '8px', border: `1px solid ${detail.color}15` }}>
                                                <DetailIcon size={14} style={{ color: detail.color, flexShrink: 0 }} />
                                                <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: detail.color }}>{detail.label}</span>
                                            </div>
                                        )}
                                        {discount.description && (
                                            <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', margin: '0.375rem 0 0.5rem 0', lineHeight: 1.45 }}>
                                                {discount.description}
                                            </p>
                                        )}
                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            {isUpcoming ? (
                                                <span style={{ color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <Calendar size={12} />
                                                    {t('discounts.comingSoon')} ({new Date(discount.startDate).toLocaleDateString('tr-TR')} {discount.startTime || ''})
                                                </span>
                                            ) : (
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <Calendar size={12} />
                                                    {t('discounts.expiryDate')}: {discount.expiryDate
                                                        ? `${new Date(discount.expiryDate).toLocaleDateString('tr-TR')}${discount.expiryTime ? ' ' + discount.expiryTime : ''}`
                                                        : t('discounts.unlimited')}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Aktif Kuponlar */}
                {addedCoupons.length > 0 && (
                    <div style={{ marginTop: '1.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.75rem', padding: '0 0.5rem' }}>
                            <Ticket size={16} style={{ color: '#3b82f6' }} />
                            <span style={{ fontSize: '0.95rem', fontWeight: 800, color: '#1d4ed8' }}>Aktif Kuponlar</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            {addedCoupons.map(coupon => {
                                const detail = getDiscountDetail(coupon);
                                const DetailIcon = detail?.icon;
                                return (
                                    <div key={coupon.id} className="card-premium" style={{
                                        borderLeft: '3px solid #3b82f6',
                                        position: 'relative'
                                    }}>
                                        <button
                                            onClick={() => removeCoupon(coupon.id)}
                                            style={{
                                                position: 'absolute', top: '10px', right: '10px',
                                                background: 'rgba(239,68,68,0.08)', border: 'none',
                                                borderRadius: '8px', padding: '4px', cursor: 'pointer',
                                                color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center'
                                            }}
                                        >
                                            <X size={16} />
                                        </button>
                                        <div style={{ flex: 1 }}>
                                            <h4 style={{ fontWeight: 800, fontSize: '1rem', margin: '0 0 0.25rem 0', paddingRight: '30px' }}>
                                                {coupon.title}
                                                <span style={{ marginLeft: '8px', fontSize: '0.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: '8px', background: '#dbeafe', color: '#1d4ed8', verticalAlign: 'middle' }}>Kupon</span>
                                            </h4>
                                            {detail && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: '0.375rem 0', padding: '6px 10px', background: `${detail.color}08`, borderRadius: '8px', border: `1px solid ${detail.color}15` }}>
                                                    <DetailIcon size={14} style={{ color: detail.color, flexShrink: 0 }} />
                                                    <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: detail.color }}>{detail.label}</span>
                                                </div>
                                            )}
                                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <Calendar size={12} />
                                                {t('discounts.expiryDate')}: {coupon.expiryDate
                                                    ? `${new Date(coupon.expiryDate).toLocaleDateString('tr-TR')}${coupon.expiryTime ? ' ' + coupon.expiryTime : ''}`
                                                    : t('discounts.unlimited')}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CustomerPortalPromotions;
